import rp, { AppComponent, BaseSidebar, BrowserEvent, ComponentState } from "@reporting/rp-app-host";
import { html, css } from "lit";
import { customElement, query } from "lit/decorators.js";
import { choose } from 'lit/directives/choose.js';
import { AnalyticsApplication, FilterContainer } from "..";
import { ReportContext } from "@reporting/rp-types/analytics";
import { openReport } from "../actions";
import { msg } from "@omegagrid/localize";

type SidebarState = {
	activeTab?: string;
	components?: Record<string, ComponentState>;
}

@customElement('rp-mf-analytics-sidebar')
export class Sidebar extends BaseSidebar<AnalyticsApplication, SidebarState> {

	static styles = [...BaseSidebar.styles, css`
		.body {
			flex: 1;
			overflow: hidden;
		}

		rp-mf-analytics-contexts {
			height: 100%;
		}

		.btn-tab {
			width: 40px;
			height: 40px;
		}
	`];

	static reactiveState = true;

	@query('.inner-component')
	innerComponent: AppComponent;

	constructor() {
		super();
		this.setState({
			activeTab: 'contexts',
			components: {}
		});
	}

	layout() {
		this.innerComponent?.layout();
	}

	_onTabClick = (e: PointerEvent) => {
		const tab = (e.target as HTMLElement).dataset.component;
		const idContext = rp.applications.get<AnalyticsApplication>('analytics').activeIdContext;
		if (tab == 'filters' && idContext) {
			this.openFilter(idContext);
		} else {
			this.updateState({activeTab: (e.target as HTMLElement).dataset.component}, true);
		}
	};

	_onBrowserSelect = (e: BrowserEvent) => {
		const id = parseInt(e.node.key);
		if (!id) return;
		const context = e.node.properties?.context as ReportContext;
		openReport(id, {
			target: 'new_tab',
			title: context?.name,
			context: context,
		});
	}

	async openFilter(idContext: number, itemIndex?: number) {
		this.updateState({activeTab: 'filters'}, true);
		await this.updateComplete;
		const filters = this.innerComponent as FilterContainer;
		filters.openFilter(idContext, itemIndex);
	}

	render = () => html`
		<div class="head">
			<og-bucket-button
				.text=${msg("analytics.views").toUpperCase()}
				icon="files"
				data-component="contexts"
				@click=${this._onTabClick}
				?active="${this.state.activeTab == "contexts"}">
			</og-bucket-button>

			<og-bucket-button
				.text=${msg("Filters").toUpperCase()}
				icon="filters"
				data-component="filters"
				@click=${this._onTabClick}
				?active="${this.state.activeTab == "filters"}">
			</og-bucket-button>

			<og-bucket-button
				.text=${msg("History").toUpperCase()}
				icon="rectangle-history"
				data-component="history"
				@click=${this._onTabClick}
				?active="${this.state.activeTab == "history"}">
			</og-bucket-button>
		</div>

		<div class="body">
			${choose(this.state.activeTab, [
				['contexts', () => html`
					<rp-mf-contexts
						class="inner-component"
						.state="${this.state.components['contexts']}"
						@state="${() => this.updateState({components: {contexts: this.innerComponent.state}})}"
						@browser.select="${this._onBrowserSelect}">
					</rp-mf-contexts>
				`], ['filters', () => html`
					<rp-filtercontainer
						class="inner-component"
						.state="${this.state.components['filters']}"
						@state="${() => this.updateState({components: {filters: this.innerComponent.state}})}">
					</rp-filtercontainer>
				`], ['history', () => html`
					<rp-mf-history
						class="inner-component"
						.state="${this.state.components['history']}"
						@state="${() => this.updateState({components: {filters: this.innerComponent.state}})}"
						@browser.select="${this._onBrowserSelect}">
					</rp-mf-history>
				`]
			])}
		</div>
	`;

}
